import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import type { DevicePrefs, PushKind } from "@luckymenu/core";
import { useCallback, useEffect, useState } from "react";
import { useStore } from "zustand";
import type { PushPermission } from "../adapters";
import { useStaffCore } from "../provider";
import { useRestaurantId } from "./use-order";

const ASKED_KEY = "luckymenu.push.asked";

/**
 * The Android app's device registration (P6-T02): once a restaurant is open
 * (not in a support session), the device registers with
 * `staff.devices.register`, with its FCM token when it has one. On the
 * first start the app asks for notifications once (Android 13 and later);
 * the answer is kept by the system. Again after a restaurant switch and
 * whenever FCM replaces the token. Sign-out removes the device
 * (`useSignOut`). Without a native push adapter (staff web) it does nothing:
 * staff web registers when notifications are turned on (`usePush`).
 */
export function useDeviceRegistration(): void {
	const core = useStaffCore();
	const adapter = core.platform.nativePush;
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const restaurantId = useRestaurantId();
	const support = useStore(core.support, (s) => s.session !== null);
	const register = useMutation(trpc.staff.devices.register.mutationOptions());
	const registerAsync = register.mutateAsync;

	useEffect(() => {
		if (!adapter || !restaurantId || support) return;
		let cancelled = false;
		const send = async (known?: string) => {
			try {
				let permission = await adapter.permission();
				if (permission === "default") {
					const asked = await core.platform.storage.getItem(ASKED_KEY).catch(() => null);
					if (!asked) {
						await core.platform.storage.setItem(ASKED_KEY, "1").catch(() => {});
						permission = await adapter.requestPermission();
					}
				}
				const token = permission === "granted" ? (known ?? (await adapter.token())) : null;
				if (cancelled) return;
				const device = await registerAsync({
					id: await core.deviceId(),
					platform: "android",
					name: core.platform.deviceName ?? null,
					fcmToken: token,
				});
				queryClient.setQueryData(trpc.staff.devices.get.queryKey({ id: device.id }), device);
			} catch {
				// Offline or refused: the next start, switch or token change tries again.
			}
		};
		void send();
		const off = adapter.onTokenChange((token) => void send(token));
		return () => {
			cancelled = true;
			off();
		};
	}, [adapter, restaurantId, support, registerAsync, core, queryClient, trpc]);
}

/**
 * More > This device (P6-T06): the on-duty switch and which pushes this
 * device gets, saved at once. `device` is null while the server does not
 * know the device: on staff web until notifications are turned on.
 */
export function useThisDevice() {
	const core = useStaffCore();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const restaurantId = useRestaurantId();
	const [id, setId] = useState<string | null>(null);
	const [permission, setPermission] = useState<PushPermission | null>(null);
	const nativePush = core.platform.nativePush;
	useEffect(() => {
		let cancelled = false;
		void core.deviceId().then((value) => {
			if (!cancelled) setId(value);
		});
		void nativePush?.permission().then((value) => {
			if (!cancelled) setPermission(value);
		});
		return () => {
			cancelled = true;
		};
	}, [core, nativePush]);
	const config = useQuery({
		...trpc.staff.devices.getPushConfig.queryOptions(),
		enabled: restaurantId !== null,
		staleTime: Number.POSITIVE_INFINITY,
	});
	const input = { id: id ?? "" };
	const queryKey = trpc.staff.devices.get.queryKey(input);
	const query = useQuery({
		...trpc.staff.devices.get.queryOptions(input),
		enabled: id !== null && restaurantId !== null,
	});
	const update = useMutation(
		trpc.staff.devices.update.mutationOptions({
			onMutate: async (change) => {
				await queryClient.cancelQueries({ queryKey });
				const before = queryClient.getQueryData(queryKey);
				queryClient.setQueryData(queryKey, (old) => {
					if (!old) return old;
					const prefs: DevicePrefs = {
						orders: change.prefs?.orders ?? old.prefs.orders,
						requests: change.prefs?.requests ?? old.prefs.requests,
						payments: change.prefs?.payments ?? old.prefs.payments,
					};
					return { ...old, onDuty: change.onDuty ?? old.onDuty, prefs };
				});
				return { before };
			},
			onError: (_error, _change, context) => {
				if (context?.before !== undefined) queryClient.setQueryData(queryKey, context.before);
			},
			onSuccess: (device) => queryClient.setQueryData(queryKey, device),
		}),
	);
	const mutate = update.mutate;
	const setOnDuty = useCallback(
		(onDuty: boolean) => {
			if (id) mutate({ id, onDuty });
		},
		[id, mutate],
	);
	const setPref = useCallback(
		(kind: PushKind, on: boolean) => {
			if (id) mutate({ id, prefs: { [kind]: on } });
		},
		[id, mutate],
	);
	return {
		device: query.data ?? null,
		/** The server sends pushes to the Android app (it has FCM credentials). */
		androidPush: config.data?.androidPush ?? false,
		/** Android's notification permission; null on the web (see `usePush`). */
		permission,
		loading: query.isPending && id !== null,
		failed: update.isError,
		setOnDuty,
		setPref,
	};
}
